import { useState } from 'react';
import { useGmbContentPerformance, useGmbEngagementInsights } from '../../hooks/useGmbOptimization';
import { GBMContentWrapper } from '../governance/shared/GMBLoadingState';
import GBMStatCard from '../governance/shared/GBMStatCard';
import GMBEmptyState from '../governance/shared/GMBEmptyState';
import type { GmbContentPerformanceResponse, GmbEngagementInsightsResponse } from '../../types';

interface Props {
  darkMode?: boolean;
  onNavigate?: (section: string) => void;
}

export default function ContentEngagementPerformance({ darkMode, onNavigate }: Props) {
  const content = useGmbContentPerformance();
  const engagement = useGmbEngagementInsights();
  const isLoading = content.isLoading || engagement.isLoading;
  const error = content.error || engagement.error;
  const refetch = () => {
    content.refetch();
    engagement.refetch();
  };

  return (
    <GBMContentWrapper error={error} onRetry={refetch} loading={isLoading}>
      <Inner
        darkMode={darkMode}
        onNavigate={onNavigate}
        content={(content.data || {}) as GmbContentPerformanceResponse}
        engagement={(engagement.data || {}) as GmbEngagementInsightsResponse}
      />
    </GBMContentWrapper>
  );
}

function Inner({ content, engagement }: Props & { content: GmbContentPerformanceResponse; engagement: GmbEngagementInsightsResponse }) {
  const [activeInsight, setActiveInsight] = useState<number | null>(null);
  const distribution = content.content_type_distribution || [];
  const postingCadence = content.posting_cadence_impact || [];
  const peakDays = engagement.peak_days || [];
  const peakHours = engagement.peak_hours || [];
  const maxTypeCount = Math.max(...distribution.map((d) => d.post_count), 1);
  const maxPosts = Math.max(...postingCadence.map((d) => d.post_count), 1);
  const maxPeakDay = Math.max(...peakDays.map((d) => d.total), 1);
  const maxPeakHour = Math.max(...peakHours.map((d) => d.interactions), 1);

  if (!distribution.length && !peakDays.length && !engagement.customer_insights?.length) {
    return <GMBEmptyState title="No content or engagement data yet" message="Publish or sync posts to populate this optimization view." icon="ri-article-line" />;
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Content Performance</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <GBMStatCard label="Posts this month" value={content.posts_this_month || 0} change="Published/scheduled" color="blue" icon="ri-article-line" />
          <GBMStatCard label="Monthly target" value={content.monthly_target || 0} change={`${Math.max((content.monthly_target || 0) - (content.posts_this_month || 0), 0)} behind`} color="orange" icon="ri-target-line" />
          <GBMStatCard label="Current streak" value={content.current_streak_days || 0} change="days" color="purple" icon="ri-fire-line" />
          <GBMStatCard label="Longest streak" value={content.longest_streak_days || 0} change="best run" color="green" icon="ri-trophy-line" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Panel title="Content Type Distribution">
            <div className="space-y-3">
              {distribution.map((type) => (
                <div key={type.type}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{type.type}</span>
                    <span className="text-xs text-gray-500">{type.post_count} posts · {type.avg_engagement_pct ?? 0}% avg engagement</span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-teal-500" style={{ width: `${(type.post_count / maxTypeCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {(content.underutilized_types || []).length > 0 && distribution.some(d => d.post_count > 0) && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <i className="ri-information-line mr-1"></i>
                  Not yet used: {content.underutilized_types.join(', ')} — consider diversifying your content mix
                </p>
              </div>
            )}
          </Panel>

          <Panel title="Posting Cadence vs KPI Impact">
            <div className="space-y-3">
              {postingCadence.map((w) => (
                <div key={w.week}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{w.week}</span>
                    <span className="text-xs text-gray-500">
                      {w.post_count} posts
                      {w.impressions != null && ` · ${w.impressions.toLocaleString()} impressions`}
                      {w.impressions_change_pct != null && (
                        <span className={w.impressions_change_pct >= 0 ? 'text-green-600' : 'text-red-500'}>
                          {' '}{w.impressions_change_pct >= 0 ? '+' : ''}{w.impressions_change_pct}%
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${(w.post_count / maxPosts) * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-gray-500 w-20 text-right">
                      {w.calls != null && `${w.calls} calls`}
                      {w.calls != null && w.directions != null && ' · '}
                      {w.directions != null && `${w.directions} dirs`}
                      {w.calls == null && w.directions == null && '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-3">Location-level KPI metrics from Google Business Profile for each period</p>
          </Panel>
        </div>

        <DataTable
          title="Periods of Inactivity"
          headers={['Period', 'Days', 'Reason', 'Impact']}
          rows={(content.periods_of_inactivity || []).map((p) => [`${p.start_date} – ${p.end_date}`, `${p.days} days`, p.reason, p.impact.replace(/_/g, ' ')])}
          emptyText="No inactivity periods found"
        />

        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-3">Best-Performing Content Themes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(content.best_performing_themes || []).map((theme) => (
            <div key={theme.theme} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">{theme.theme}</h4>
                {theme.avg_engagement_pct != null ? (
                  <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-medium">{theme.avg_engagement_pct}% avg</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium">Post volume leader</span>
                )}
              </div>
              <p className="text-xs text-gray-500">Top: <span className="text-gray-700 dark:text-gray-300 font-medium">{theme.top_post_title || '—'}</span></p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Engagement Insights</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {(engagement.action_share || []).filter(item => item.pct > 0).map((item) => (
            <GBMStatCard
              key={item.action_type}
              label={item.action_type.replace(/_/g, ' ')}
              value={`${item.pct}%`}
              change={item.trend === 'up' ? 'Trending up' : item.trend === 'down' ? 'Trending down' : 'Stable'}
              color={item.trend === 'down' ? 'red' : item.trend === 'up' ? 'green' : 'blue'}
              icon={item.action_type === 'calls' ? 'ri-phone-line' : item.action_type === 'directions' ? 'ri-map-pin-line' : 'ri-message-3-line'}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Panel title="When Customers Leave Reviews">
            <div className="space-y-3">
              {peakDays.map((d) => (
                <StackedBar key={d.day} label={d.day} total={d.total} max={maxPeakDay} calls={d.calls} directions={d.directions} messages={d.messages} />
              ))}
            </div>
          </Panel>
          <Panel title="Peak Review Hours">
            <div className="space-y-3">
              {peakHours.map((h) => (
                <div key={h.hour_slot}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{h.hour_slot}</span>
                    <span className="text-xs text-gray-500">{h.interactions} reviews</span>
                  </div>
                  <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(h.interactions / maxPeakHour) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">Review timestamps help optimize your response timing — reply promptly during peak review windows</p>

        <DataTable
          title="Seasonal Engagement Spikes"
          headers={['Period', 'Months', 'Lift', 'Top Action']}
          rows={(engagement.seasonal_spikes || []).map((s) => [s.period, s.months, `${s.lift_pct}%`, s.top_action])}
          emptyText="Sync your GMB profile at least twice to detect seasonal patterns"
        />

        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-3">Customer Interaction Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(engagement.customer_insights || []).map((insight, i) => (
            <button
              key={`${insight.insight_text}-${i}`}
              onClick={() => setActiveInsight(activeInsight === i ? null : i)}
              className={`text-left bg-white dark:bg-gray-800 rounded-xl border p-4 shadow-sm transition-colors ${
                activeInsight === i ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-teal-600 text-lg"></i>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">“{insight.insight_text}”</p>
                  {activeInsight === i && <p className="mt-2 text-xs text-teal-700 dark:text-teal-300">Prioritize this insight in your next content and profile updates.</p>}
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-teal-50 text-teal-700">{insight.priority}</span>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function StackedBar({ label, total, max, calls, directions, messages }: { label: string; total: number; max: number; calls?: number | null; directions?: number | null; messages?: number | null }) {
  const hasBreakdown = calls != null || directions != null || messages != null;
  const c = calls ?? 0;
  const d = directions ?? 0;
  const m = messages ?? 0;
  const safeTotal = Math.max(total, 1);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{label}</span>
        <span className="text-xs text-gray-500">{total} reviews</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex" style={{ maxWidth: `${(total / max) * 100}%` }}>
          {hasBreakdown ? (
            <>
              <div className="h-full bg-orange-500" style={{ width: `${(c / safeTotal) * 100}%` }} />
              <div className="h-full bg-purple-500" style={{ width: `${(d / safeTotal) * 100}%` }} />
              <div className="h-full bg-blue-500" style={{ width: `${(m / safeTotal) * 100}%` }} />
            </>
          ) : (
            <div className="h-full bg-teal-500 rounded-full" style={{ width: '100%' }} />
          )}
        </div>
      </div>
    </div>
  );
}

function DataTable({ title, headers, rows, emptyText }: { title: string; headers: string[]; rows: string[][]; emptyText: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      </div>
      <table className="w-full">
        <thead><tr>{headers.map((h) => <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={headers.length} className="px-4 py-4 text-xs text-gray-500">{emptyText}</td></tr> : rows.map((row, i) => (
            <tr key={i} className="border-t border-gray-50 dark:border-gray-700/50">{row.map((cell, j) => <td key={j} className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300 capitalize">{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
